import { CSSInterpolation } from '@emotion/css/create-instance'
import React, { type ComponentProps, type ReactNode } from 'react'

import { TextButton } from './TextButton'
import { Type } from './Type'

type Props = {
  /**
   * The short form of the text to be shown.
   */
  previewText: ReactNode
  /**
   * The full expanded text to be shown.
   */
  fullText: ReactNode
  /**
   * Flag to indicate whether we want to show preview or full text.
   */
  isExpanded: boolean
  /**
   * Callback when Text Button is clicked on to show more/less.
   */
  onToggleExpand: (currentIsExpanded: boolean) => void
  /**
   * Optional label to use for text button when component is collapsed.
   */
  showMoreLabel?: string
  /**
   * Optional label to use for text button when component is expanded.
   */
  showLessLabel?: string

  /**
   * Optional state for text button for use cases such as loading text.
   */
  textButtonState?: ComponentProps<typeof TextButton>['state']
  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
  /**
   * Optional override for text button color.
   */
  textButtonColorOverride?: ComponentProps<typeof TextButton>['color']
}

/**
 * CollapsibleText is an expandable paragraph in which users can toggle the visibility of content by clicking on an inline TextButton.
 */
export function CollapsibleText({
  fullText,
  isExpanded,
  onToggleExpand,
  overrideRootStyles = {},
  previewText,
  showLessLabel = 'Show less',
  showMoreLabel = 'Show more',
  textButtonState = false,
  textButtonColorOverride,
}: Props) {
  return (
    <div css={overrideRootStyles}>
      <Type.Text size="M">{isExpanded ? fullText : previewText}</Type.Text>
      &nbsp;
      <TextButton
        aria-expanded={!!isExpanded}
        color={textButtonColorOverride}
        label={isExpanded ? showLessLabel : showMoreLabel}
        onClick={() => onToggleExpand(isExpanded)}
        state={textButtonState}
        type="button"
      />
    </div>
  )
}
